import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  ChartV2Handle,
  ChartV2HoverHit,
  ChartV2PriceDisplay,
  ChartV2ResolvedSeries,
} from '../types/chart-v2.types';
import {
  clamp,
  devicePixelRatioSafe,
  indexAtTime,
  timeAtIndex,
} from '../utils/helpers';
import { rebaseToPercentage } from '../utils/rebase';
import styles from './ChartCanvas.module.scss';
import {
  clearCanvas,
  drawGrid,
  drawHoverOverlay,
  drawLatestPriceOverlay,
  drawSeries,
  drawXAxis,
  drawYAxes,
  HoverHit,
  resolveThemeFromElement,
  Y_AXIS_COLUMN_WIDTH,
} from './draw';
import { hitTestAll } from './hit-test';
import {
  buildBarTimeline,
  createScales,
  DEFAULT_CANVAS_PADDING,
  Viewport,
} from './scales';

const MIN_SPAN_MS = 1000 * 60;
/**
 * Max zoom-out window as a multiple of the raw data span. Matches the
 * right-edge pan clamp (`end ≤ dataEnd + span/2`) with the left edge
 * pinned to data start: span ≤ 2 × dataSpan. Also keeps the 15% future
 * pad from `computeInitialDomain` under the ceiling so the first wheel
 * tick on a padded MAX view doesn't collapse it.
 */
const MAX_ZOOM_OUT_DATA_SPAN_FACTOR = 2;

const panIndexed = ({
  tl,
  startDomain,
  dx,
  plotWidth,
}: {
  tl: number[];
  startDomain: [number, number];
  dx: number;
  plotWidth: number;
}): [number, number] => {
  const startIdx = indexAtTime(tl, startDomain[0]);
  const endIdx = indexAtTime(tl, startDomain[1]);
  const visibleSpan = Math.max(endIdx - startIdx, 1e-6);
  // Fractional (not rounded) index shift — rounding to whole bars is what
  // made panning feel "steppy": the drag wouldn't move the chart at all
  // until `dx` had accumulated enough to round to the next bar.
  const indexShift = -(dx / plotWidth) * visibleSpan;

  return [
    timeAtIndex(tl, startIdx + indexShift),
    timeAtIndex(tl, endIdx + indexShift),
  ];
};

interface ZoomIndexedArgs {
  tl: number[];
  domain: [number, number];
  pivotTime: number;
  direction: number;
}

const zoomIndexed = ({
  tl,
  domain,
  pivotTime,
  direction,
}: ZoomIndexedArgs): [number, number] => {
  const domainStartIdx = indexAtTime(tl, domain[0]);
  const domainEndIdx = indexAtTime(tl, domain[1]);
  const visibleSpan = Math.max(domainEndIdx - domainStartIdx, 1e-6);
  const dataBars = Math.max(MIN_VISIBLE_BARS, tl.length - 1);
  // Fractional (not rounded) span — keeping this continuous means there's
  // no integer dead-zone where `ZOOM_STEP` rounds back to the same bar
  // count and the wheel appears to do nothing.
  const newSpan = clamp(
    visibleSpan * direction,
    MIN_VISIBLE_BARS,
    dataBars * MAX_ZOOM_OUT_DATA_SPAN_FACTOR
  );
  const pivotIdx = indexAtTime(tl, pivotTime);
  const pivotFraction = (pivotIdx - domainStartIdx) / visibleSpan;
  const newStartIdx = pivotIdx - pivotFraction * newSpan;
  const newEndIdx = newStartIdx + newSpan;

  return [timeAtIndex(tl, newStartIdx), timeAtIndex(tl, newEndIdx)];
};

const MIN_VISIBLE_BARS = 5;
// Wheel-zoom step per tick. Trackpads tend to fire many small `wheel`
// events with `deltaY` values in the 1-10 range, so anything ≥0.1 makes
// pinch-zoom feel runaway-fast. 0.06 keeps a single mouse-wheel notch
// (`deltaY = 100`) visibly responsive without overshooting on trackpads.
const ZOOM_STEP = 0.06;

/**
 * Slide a fixed-width window back inside `[lo, hi + span/2]` — clamping one
 * edge drags the other along so panning stays rigid. The right bound mirrors
 * v1's `translateExtent`: the last point stops at the viewport centre.
 */
const clampWindow = (
  [start, end]: [number, number],
  lo: number,
  hi: number
): [number, number] => {
  const span = Math.max(end - start, 1e-6);
  // The upper bound is floored at `lo` so a window wider than the pannable
  // range (possible on timelines shorter than `MIN_VISIBLE_BARS`) keeps the
  // left edge pinned to data start rather than sliding past it.
  const nextStart = clamp(start, lo, Math.max(lo, hi - span / 2));

  return [nextStart, nextStart + span];
};

/**
 * Same clamp as `clampWindow`, applied in whichever space the x-scale maps
 * by. Bar-timeline charts position bars by index, so the half-window has to
 * be measured there too — wall-clock `span/2` is inflated by overnight gaps
 * and would let the last bar drift well past the viewport centre.
 */
const clampDomainToDataExtent = ({
  nextStart,
  nextEnd,
  dataExtent,
  barTimeline,
}: {
  nextStart: number;
  nextEnd: number;
  dataExtent: { start: number; end: number };
  barTimeline: number[];
}): [number, number] => {
  if (barTimeline.length < 2) {
    return clampWindow([nextStart, nextEnd], dataExtent.start, dataExtent.end);
  }

  const at = (ms: number) => indexAtTime(barTimeline, ms);
  const [startIdx, endIdx] = clampWindow(
    [at(nextStart), at(nextEnd)],
    at(dataExtent.start),
    at(dataExtent.end)
  );

  return [timeAtIndex(barTimeline, startIdx), timeAtIndex(barTimeline, endIdx)];
};

/** Payload emitted by `ChartCanvasProps.onContextMenu`. */
export interface ChartCanvasContextMenuEvent {
  /** Viewport-relative x (CSS pixels). Same coordinate space as `MouseEvent.clientX`. */
  clientX: number;
  /** Viewport-relative y (CSS pixels). Same coordinate space as `MouseEvent.clientY`. */
  clientY: number;
  /** True when the right-click originated over the y-axis column region. */
  isWithinYAxis: boolean;
}

export interface ChartCanvasHoverPayload {
  time: number;
  clientX: number;
  clientY: number;
  hits: ChartV2HoverHit[];
}

/**
 * Props for `<ChartCanvas>` — the standalone canvas engine. Use this
 * directly when you want a chart canvas without the top-bar / legend
 * shell that `<ChartV2>` provides.
 *
 * Required: `series`, `initialDomain`, `dataExtent`. Most callbacks are
 * optional and only need wiring when you want to observe interactions.
 */
export interface ChartCanvasProps {
  /**
   * Resolved series to draw. Use the `ChartV2ResolvedSeries` shape
   * directly — colour and ordering must already be decided by the host.
   */
  series: ChartV2ResolvedSeries[];
  /** Visible time domain on first paint. UTC ms. */
  initialDomain: { start: number; end: number };
  /**
   * Outer bounds of all series data (UTC ms). Pan/zoom clamps the left
   * edge to `start`, and the right edge to `end + visibleSpan/2` so at
   * most half the viewport can sit past the last data point (matches v1).
   */
  dataExtent: { start: number; end: number };
  /** Override the default canvas padding. */
  padding?: Partial<Viewport['padding']>;
  /** Localisable strings. Currently only the no-data message. */
  locales?: { noData?: string };
  /**
   * Called whenever the visible time domain changes (pan, wheel-zoom,
   * imperative `setDomain`). Times are UTC ms.
   */
  onDomainChange?: (domain: { start: number; end: number }) => void;
  /**
   * Whether to draw the x-axis at the bottom of the canvas. Hide it on
   * stacked panes other than the bottom one.
   *
   * @default true
   */
  showXAxis?: boolean;
  /** Externally-controlled crosshair time. UTC ms or `null` for no hover. */
  hoverTime?: number | null;
  /** Called when the crosshair moves over the canvas. */
  onHoverTimeChange: (time: number | null) => void;
  /** Called with snapped hover payload for custom tooltips. */
  onHoverChange?: (hover: ChartCanvasHoverPayload | null) => void;
  /**
   * When true, draws a dashed reference line at each series' latest
   * value plus a colour-matched price pill on the y-axis column.
   *
   * @default false
   */
  showLatestPrice?: boolean;
  /**
   * Per-pane price-display mode. `indexed` rebases every series to its
   * first visible value (expressed as %) and collapses to a single
   * shared y-axis.
   *
   * @default 'price'
   */
  priceDisplay?: ChartV2PriceDisplay;
  /**
   * Number of y-axis columns to reserve on the right. When multiple
   * panes have different series counts, the orchestrator should pass
   * the max column count to every canvas so plot widths (and therefore
   * x-scales and crosshair positions) stay aligned across panes.
   *
   * @default the pane's own series count
   */
  axisColumnCount?: number;
  /**
   * Width (px) of each reserved y-axis column. Sized by the orchestrator
   * to fit the widest tick label so long values aren't clipped, and shared
   * across panes so plot widths stay aligned.
   *
   * @default `Y_AXIS_COLUMN_WIDTH`
   */
  axisColumnWidth?: number;
  /**
   * Fires on right-click. Consumers render their own context menu using
   * the returned client coordinates. Chartv2 suppresses the default
   * browser menu.
   */
  onContextMenu?: (event: ChartCanvasContextMenuEvent) => void;
  /**
   * Enable drag-to-pan and wheel-zoom. When `false` the canvas shows a
   * fixed window (driven entirely by `initialDomain`) while hover /
   * crosshair stays active.
   *
   * @default true
   */
  enableZoom?: boolean;
  /**
   * Crosshair style on hover. `'default'` shows the full crosshair
   * (horizontal guide + y-axis price pill + x-axis date pill).
   * `'simple'` mirrors v1's simple chart: vertical line plus a dot on
   * each line/area series, with no horizontal guide or pills.
   *
   * @default 'default'
   */
  hoverVariant?: 'default' | 'simple';
  /**
   * Gap-collapsing bar timestamps for the x-scale. When omitted, built
   * from `series`. Pass the pane's full series list (including hidden)
   * so hide keeps the same index scale as insights — v1 parity.
   */
  barTimeline?: number[];
}

const getViewport = (
  width: number,
  height: number,
  padding: Viewport['padding']
): Viewport => ({ width, height, padding });

/**
 * Standalone canvas drawing surface for chartv2. Renders the grid,
 * axes, series (line / area / candle / bar), crosshair, and optional
 * latest-price overlay; handles pan, wheel-zoom, hover hit-testing,
 * and the imperative `ChartV2Handle` API (`resetZoom`, `setDomain`,
 * `exportImage`).
 *
 * Use this directly when you need a chart canvas without the
 * top-bar / legend chrome that `<ChartV2>` provides. The host owns
 * series colours and ordering — pass `ChartV2ResolvedSeries`, not the
 * raw `ChartV2Series`.
 */
export const ChartCanvas = forwardRef<ChartV2Handle, ChartCanvasProps>(
  (props, ref) => {
    const {
      series: rawSeries,
      initialDomain,
      dataExtent,
      padding,
      locales,
      onDomainChange,
      showXAxis = true,
      hoverTime = null,
      onHoverTimeChange,
      onHoverChange,
      showLatestPrice = false,
      priceDisplay = 'price',
      axisColumnCount,
      axisColumnWidth,
      onContextMenu,
      enableZoom = true,
      hoverVariant = 'default',
      barTimeline: barTimelineProp,
    } = props;

    const resolvedColumnWidth = axisColumnWidth ?? Y_AXIS_COLUMN_WIDTH;

    const rootRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [domain, setDomain] = useState<[number, number]>([
      initialDomain.start,
      initialDomain.end,
    ]);
    const [viewport, setViewport] = useState<{
      width: number;
      height: number;
    }>({ width: 0, height: 0 });
    const dragStateRef = useRef<{
      startX: number;
      startDomain: [number, number];
    } | null>(null);

    useEffect(() => {
      setDomain(prev =>
        prev[0] === initialDomain.start && prev[1] === initialDomain.end
          ? prev
          : [initialDomain.start, initialDomain.end]
      );
    }, [initialDomain.start, initialDomain.end]);

    useImperativeHandle(
      ref,
      () => ({
        resetZoom: () => {
          setDomain([initialDomain.start, initialDomain.end]);
          onDomainChange?.({
            start: initialDomain.start,
            end: initialDomain.end,
          });
        },
        setDomain: range => {
          setDomain([range.start, range.end]);
          onDomainChange?.(range);
        },
        exportImage: () => canvasRef.current?.toDataURL('image/png'),
      }),
      [initialDomain.start, initialDomain.end, onDomainChange]
    );

    useLayoutEffect(() => {
      const node = rootRef.current;

      if (!node) {
        return undefined;
      }

      const observer = new ResizeObserver(entries => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        const { width, height } = entry.contentRect;

        setViewport({ width, height });
      });

      observer.observe(node);
      return () => observer.disconnect();
    }, []);

    const isIndexed = priceDisplay === 'indexed';

    // When indexed: rebase every series to `% change from first visible
    // value`. The rebase depends on domain, so it must recompute on pan/zoom.
    const series = useMemo<ChartV2ResolvedSeries[]>(() => {
      if (!isIndexed) {
        return rawSeries;
      }

      return rawSeries.map(s => rebaseToPercentage(s, domain));
    }, [rawSeries, isIndexed, domain]);

    const barTimeline = useMemo(
      () => barTimelineProp ?? buildBarTimeline(rawSeries),
      [barTimelineProp, rawSeries]
    );

    const effectivePadding = useMemo(() => {
      // Prefer the caller-supplied column count so every pane reserves the
      // same right-padding (keeps plot.width — and therefore x-scales,
      // crosshair, x-axis ticks — aligned across panes). Fall back to this
      // pane's own series count when the orchestrator doesn't care.
      const ownColumnCount = isIndexed ? 1 : Math.max(series.length, 1);
      const columnCount = Math.max(axisColumnCount ?? ownColumnCount, 1);
      const axisColumnsWidth = columnCount * resolvedColumnWidth;
      const merged = { ...DEFAULT_CANVAS_PADDING, ...(padding || {}) };
      const bottom = showXAxis ? merged.bottom : Math.min(merged.bottom, 8);
      // The default top padding reserves room for the y-axis header pill
      // (drawn just above the plot). When no column has a header label —
      // e.g. the simple chart sets `yAxisLabel: ''` — nothing renders there,
      // so reclaim the space instead of leaving dead headroom above the data.
      const hasAxisHeaderLabel =
        isIndexed || series.some(s => (s.yAxisLabel ?? s.label) !== '');
      const top = hasAxisHeaderLabel ? merged.top : Math.min(merged.top, 8);

      return { ...merged, top, bottom, right: merged.right + axisColumnsWidth };
    }, [
      padding,
      series,
      isIndexed,
      axisColumnCount,
      resolvedColumnWidth,
      showXAxis,
    ]);

    const scales = useMemo(() => {
      if (viewport.width === 0 || viewport.height === 0) {
        return null;
      }

      const vp = getViewport(viewport.width, viewport.height, effectivePadding);

      return createScales({
        viewport: vp,
        xDomain: domain,
        seriesWithColors: series.map(s => ({
          series: s,
          color: s.color,
        })),
        mergeYDomain: isIndexed,
        barTimeline,
      });
    }, [
      viewport.width,
      viewport.height,
      series,
      domain,
      effectivePadding,
      isIndexed,
      barTimeline,
    ]);

    useLayoutEffect(() => {
      const canvas = canvasRef.current;
      const rootEl = rootRef.current;

      if (!canvas || !rootEl || !scales || viewport.width === 0) {
        return;
      }

      const dpr = devicePixelRatioSafe();
      const targetWidth = Math.round(viewport.width * dpr);
      const targetHeight = Math.round(viewport.height * dpr);

      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
      }

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return;
      }

      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const theme = resolveThemeFromElement(rootEl);
      const vp = getViewport(viewport.width, viewport.height, effectivePadding);

      clearCanvas(ctx, vp, theme);

      const dc = {
        ctx,
        scales,
        theme,
        viewport: vp,
        showXAxis,
        columnWidth: resolvedColumnWidth,
        mergedYAxis: isIndexed,
      };
      const yAxisLabels = series.map(s => s.yAxisLabel ?? s.label);
      const tooltipLabels = series.map(s => s.label);

      const hasAnyPoints = series.some(s => s.data.length > 0);
      // Empty series list = all pane series hidden; keep axes as a placeholder.
      const isPlaceholder = series.length === 0;

      if (hasAnyPoints || isPlaceholder) {
        drawGrid(dc);

        if (showXAxis) {
          drawXAxis(dc);
        }

        drawYAxes(
          dc,
          yAxisLabels,
          isIndexed ? { merged: true, mergedLabel: '%' } : undefined
        );

        if (hasAnyPoints) {
          drawSeries(dc, series);
        }
      }

      // Per-series opt-in: `showLatestPrice` on the series itself (not a
      // global flag) so the consumer can turn the overlay off for
      // non-price series (ratios, indicators) where a "current value"
      // reference would be noise. `showLatestPrice` on the canvas props
      // is treated as a legacy override that forces it on for all series.
      const latestValues = series.map(s => {
        const enabled = showLatestPrice || s.showLatestPrice;

        if (!enabled) {
          return undefined;
        }

        const last = s.data[s.data.length - 1];

        if (!last) {
          return undefined;
        }

        return 'close' in last ? last.close : last.value;
      });

      if (latestValues.some(v => v !== undefined)) {
        drawLatestPriceOverlay(dc, { values: latestValues });
      }

      if (
        hoverTime != null &&
        hoverTime >= domain[0] &&
        hoverTime <= domain[1]
      ) {
        const rawHits = hitTestAll(series, hoverTime);
        // Snap the crosshair to the nearest data-point time so it ticks
        // discretely (like v1) instead of drifting continuously with the
        // pointer. Fall back to raw hoverTime when no hits are available.
        const snappedTime = rawHits[0]?.point.time ?? hoverTime;
        const hits: HoverHit[] = rawHits.map(h => ({
          seriesId: h.seriesId,
          color: h.color,
          seriesType: h.seriesType,
          value: 'close' in h.point ? h.point.close : h.point.value,
        }));

        // Infer bar interval from consecutive data points — mirrors v1's
        // `isIntradayResample` flag without needing an explicit prop.
        // If the step between bars is < 1 day, the data is intraday.
        const DAY_MS = 1_000 * 60 * 60 * 24;
        const firstSeries = series.find(s => s.data.length >= 2);
        const barStepMs = firstSeries
          ? firstSeries.data[1].time - firstSeries.data[0].time
          : 0;
        const isIntraday = barStepMs > 0 && barStepMs < DAY_MS;

        drawHoverOverlay(dc, {
          hoverTime: snappedTime,
          hits,
          seriesLabels: tooltipLabels,
          showTimeLabel: showXAxis,
          isIntraday,
          variant: hoverVariant,
        });
      }

      ctx.restore();
    }, [
      scales,
      series,
      viewport.width,
      viewport.height,
      effectivePadding,
      hoverTime,
      domain,
      showXAxis,
      showLatestPrice,
      isIndexed,
      resolvedColumnWidth,
      hoverVariant,
    ]);

    const toTimeFromX = useCallback(
      (x: number): number | null => {
        if (!scales) {
          return null;
        }

        const { plot } = scales;
        const clamped = clamp(x, plot.x, plot.x + plot.width);

        return scales.xInvert(clamped);
      },
      [scales]
    );

    // Compute up-front so the interaction handlers can short-circuit.
    // `series` is always defined here; this also covers the "all
    // series have empty data arrays" case (initial load, between
    // websocket frames, etc.).
    const hasAnyPoints = series.some(s => s.data.length > 0);

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!scales || !rootRef.current || !hasAnyPoints) {
        return;
      }

      const rect = rootRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;

      if (dragStateRef.current) {
        const dx = x - dragStateRef.current.startX;
        const startDomain = dragStateRef.current.startDomain;
        const span = startDomain[1] - startDomain[0];

        let nextStart: number;
        let nextEnd: number;

        if (scales.barTimeline.length >= 2) {
          [nextStart, nextEnd] = panIndexed({
            tl: scales.barTimeline,
            startDomain,
            dx,
            plotWidth: scales.plot.width,
          });
        } else {
          const shiftMs = -(dx / scales.plot.width) * span;
          nextStart = startDomain[0] + shiftMs;
          nextEnd = startDomain[1] + shiftMs;
        }

        [nextStart, nextEnd] = clampDomainToDataExtent({
          nextStart,
          nextEnd,
          dataExtent,
          barTimeline: scales.barTimeline,
        });

        setDomain([nextStart, nextEnd]);
        onDomainChange?.({ start: nextStart, end: nextEnd });
        return;
      }

      const time = toTimeFromX(x);
      onHoverTimeChange(time);

      if (!onHoverChange) {
        return;
      }

      if (time == null) {
        onHoverChange(null);
        return;
      }

      const rawHits = hitTestAll(series, time);
      const snappedTime = rawHits[0]?.point.time ?? time;
      const hoverTopClientY = rawHits.reduce((min, hit) => {
        const scale = scales.perSeries.find(
          item => item.seriesId === hit.seriesId
        );
        const pointValue =
          'close' in hit.point ? hit.point.close : hit.point.value;

        if (!scale) {
          return min;
        }

        const y = scale.yScale(pointValue);

        if (!Number.isFinite(y)) {
          return min;
        }

        return Math.min(min, y);
      }, Number.POSITIVE_INFINITY);

      onHoverChange({
        time: snappedTime,
        clientX: e.clientX,
        clientY: Number.isFinite(hoverTopClientY)
          ? rect.top + hoverTopClientY
          : e.clientY,
        hits: rawHits.map(hit => ({
          seriesId: hit.seriesId,
          color: hit.color,
          value: 'close' in hit.point ? hit.point.close : hit.point.value,
          time: hit.point.time,
        })),
      });
    };

    const handlePointerLeave = () => {
      onHoverTimeChange(null);
      onHoverChange?.(null);

      // If the pointer leaves mid-drag we still need to flush the
      // final domain to the parent — otherwise consumers tracking the
      // visible window (e.g. live-feed throttling, URL state) can
      // miss the last position the user dragged to.
      if (dragStateRef.current) {
        onDomainChange?.({ start: domain[0], end: domain[1] });
      }

      dragStateRef.current = null;
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!scales || !rootRef.current || !hasAnyPoints || !enableZoom) {
        return;
      }

      const rect = rootRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;

      dragStateRef.current = {
        startX: x,
        startDomain: [...domain],
      };

      if (e.target instanceof Element) {
        e.target.setPointerCapture(e.pointerId);
      }
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragStateRef.current) {
        onDomainChange?.({ start: domain[0], end: domain[1] });
      }

      dragStateRef.current = null;

      try {
        if (e.target instanceof Element) {
          e.target.releasePointerCapture(e.pointerId);
        }
      } catch {
        // ignore
      }
    };

    useEffect(() => {
      const node = rootRef.current;

      // Skip wheel-zoom binding entirely when the chart is empty —
      // there's nothing to zoom and the `event.preventDefault()` would
      // still swallow page scroll, which is the opposite of what the
      // user expects on an empty placeholder. Also skip when zoom is
      // disabled so the page can scroll over a fixed-window chart.
      if (!node || !scales || !hasAnyPoints || !enableZoom) {
        return undefined;
      }

      const onWheel = (event: WheelEvent) => {
        event.preventDefault();

        const rect = node.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const pivotTime = toTimeFromX(x);

        if (pivotTime == null) {
          return;
        }

        const direction = event.deltaY > 0 ? 1 + ZOOM_STEP : 1 - ZOOM_STEP;

        let nextStart: number;
        let nextEnd: number;

        if (scales.barTimeline.length >= 2) {
          [nextStart, nextEnd] = zoomIndexed({
            tl: scales.barTimeline,
            domain,
            pivotTime,
            direction,
          });
        } else {
          const span = domain[1] - domain[0];
          const dataSpan = Math.max(
            MIN_SPAN_MS,
            dataExtent.end - dataExtent.start
          );
          // Cap at 2× data span — same ceiling as the 50% future right-edge
          // clamp — so a padded MAX overview (1.15×) isn't collapsed by the
          // first wheel tick.
          const newSpan = Math.min(
            dataSpan * MAX_ZOOM_OUT_DATA_SPAN_FACTOR,
            Math.max(MIN_SPAN_MS, span * direction)
          );
          const pivotFraction = (pivotTime - domain[0]) / span;
          nextStart = pivotTime - pivotFraction * newSpan;
          nextEnd = nextStart + newSpan;
        }

        [nextStart, nextEnd] = clampDomainToDataExtent({
          nextStart,
          nextEnd,
          dataExtent,
          barTimeline: scales.barTimeline,
        });

        setDomain([nextStart, nextEnd]);
        onDomainChange?.({ start: nextStart, end: nextEnd });
      };

      node.addEventListener('wheel', onWheel, { passive: false });
      return () => node.removeEventListener('wheel', onWheel);
    }, [
      scales,
      domain,
      dataExtent,
      onDomainChange,
      toTimeFromX,
      hasAnyPoints,
      enableZoom,
    ]);

    const handleContextMenu = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (!onContextMenu || !hasAnyPoints) {
          return;
        }

        const rect = rootRef.current?.getBoundingClientRect();
        const plot = scales?.plot;

        // `isWithinYAxis` mirrors the original chart: any x beyond the
        // plot area's right edge is considered the y-axis column.
        const isWithinYAxis =
          rect && plot
            ? event.clientX - rect.left > plot.x + plot.width
            : false;

        event.preventDefault();
        onContextMenu({
          clientX: event.clientX,
          clientY: event.clientY,
          isWithinYAxis,
        });
      },
      [onContextMenu, scales, hasAnyPoints]
    );

    return (
      <div
        ref={rootRef}
        className={styles.root}
        data-testid="chartv2-canvas-root"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onContextMenu={handleContextMenu}
      >
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          data-testid="chartv2-canvas"
        />
        {!hasAnyPoints && series.length > 0 && (
          <div className={styles.empty} data-testid="chartv2-empty">
            {locales?.noData ?? 'No data'}
          </div>
        )}
      </div>
    );
  }
);

ChartCanvas.displayName = 'ChartCanvas';
